/**
 * Shared "Hours & Setup" editor (Tenant Admin + Merchant).
 *
 * Both consoles render the EXACT same UI and write through their own
 * scoped PATCH endpoint:
 *   - Merchant: PATCH /merchant/restaurants/{rid}
 *   - Admin:    PATCH /admin/restaurants/{rid}
 * Both endpoints update the SAME ``restaurants`` Mongo doc, so a save
 * on either side is immediately visible to the other.
 *
 * Multi-shift business hours per Phase 3 (`shifts: [{open, close}]`),
 * with legacy ``{open, close}`` auto-upgraded on hydrate so old data
 * doesn't break.
 */
import { useEffect, useRef, useState } from "react";
import { Loader2, Save, Printer, Plus, X, Copy } from "lucide-react";
import { api } from "../lib/api";
import { buildHoursSaveBody } from "../lib/hoursSaveBody";
import { toast } from "sonner";
import AdminShopPicker from "./AdminShopPicker";

const DAYS = [
    { k: "mon", l: "Monday" }, { k: "tue", l: "Tuesday" }, { k: "wed", l: "Wednesday" },
    { k: "thu", l: "Thursday" }, { k: "fri", l: "Friday" }, { k: "sat", l: "Saturday" }, { k: "sun", l: "Sunday" },
];

/** Normalise an incoming day config to the multi-shift shape. */
function toMultiShift(cfg) {
    if (!cfg) return { closed: false, shifts: [{ open: "09:00", close: "23:00" }] };
    if (Array.isArray(cfg.shifts) && cfg.shifts.length > 0) {
        return {
            closed: !!cfg.closed,
            shifts: cfg.shifts.map((s) => ({ open: s.open || "09:00", close: s.close || "23:00" })),
        };
    }
    return {
        closed: !!cfg.closed,
        shifts: [{ open: cfg.open || "09:00", close: cfg.close || "23:00" }],
    };
}

/** The editable sections of a shop, in the exact shape the PATCH endpoints accept. */
function sectionsOf(shop) {
    const existing = shop?.business_hours || {};
    const business_hours = {};
    for (const { k } of DAYS) business_hours[k] = toMultiShift(existing[k]);
    return {
        business_hours,
        printer_config: shop?.printer_config || {},
        payout_account: shop?.payout_account || {},
        home_delivery_enabled: shop?.home_delivery_enabled !== false,
        pickup_enabled: shop?.pickup_enabled !== false,
    };
}

export default function HoursSetupEditor({ apiPrefix, testidPrefix = "hours" }) {
    const [rests, setRests] = useState([]);
    const [rid, setRid] = useState("");
    const [r, setR] = useState(null);
    const [hours, setHours] = useState({});
    const [printerCfg, setPrinterCfg] = useState({});
    const [payout, setPayout] = useState({});
    const [services, setServices] = useState({ home_delivery_enabled: true, pickup_enabled: true });
    const [busy, setBusy] = useState(false);
    // What the form was hydrated from — save diffs against it so untouched sections/days are never re-sent.
    const loaded = useRef(null);

    useEffect(() => {
        api.get(`${apiPrefix}/restaurants`).then(({ data }) => {
            setRests(data || []);
            if (data?.[0]) setRid(data[0].id);
        });
    }, [apiPrefix]);

    useEffect(() => {
        if (!rid) return;
        const found = rests.find((x) => x.id === rid);
        if (!found) return;
        // Defer state-update so React 19's set-state-in-effect lint passes.
        Promise.resolve().then(() => {
            const sections = sectionsOf(found);
            loaded.current = sections;
            setR(found);
            setHours(sections.business_hours);
            setPrinterCfg(sections.printer_config);
            setPayout(sections.payout_account);
            setServices({
                home_delivery_enabled: sections.home_delivery_enabled,
                pickup_enabled: sections.pickup_enabled,
            });
        });
    }, [rid, rests]);

    const setDay = (k, patch) => setHours((prev) => ({ ...prev, [k]: { ...prev[k], ...patch } }));

    const addShift = (k) => {
        const d = hours[k] || { shifts: [] };
        setDay(k, { shifts: [...(d.shifts || []), { open: "16:00", close: "21:00" }] });
    };
    const removeShift = (k, idx) => {
        const d = hours[k] || { shifts: [] };
        const next = d.shifts.filter((_, i) => i !== idx);
        setDay(k, { shifts: next.length ? next : [{ open: "09:00", close: "23:00" }] });
    };
    const updateShift = (k, idx, patch) => {
        const d = hours[k] || { shifts: [] };
        setDay(k, { shifts: d.shifts.map((s, i) => (i === idx ? { ...s, ...patch } : s)) });
    };

    const copyMonToAll = () => {
        const mon = hours.mon;
        if (!mon) return;
        const cloned = { closed: !!mon.closed, shifts: mon.shifts.map((s) => ({ ...s })) };
        const next = {};
        for (const { k } of DAYS) next[k] = k === "mon" ? mon : cloned;
        setHours(next);
        toast.success("Monday hours copied to every weekday");
    };

    const save = async () => {
        if (!rid || !loaded.current) return;
        setBusy(true);
        try {
            // Re-read the shop first: another console may have saved since this page loaded it.
            const { data: latestRows } = await api.get(`${apiPrefix}/restaurants`);
            const latest = (latestRows || []).find((x) => x.id === rid);
            if (!latest) { toast.error("This shop is no longer available"); return; }
            const body = buildHoursSaveBody(loaded.current, {
                business_hours: hours,
                printer_config: printerCfg,
                payout_account: payout,
                home_delivery_enabled: services.home_delivery_enabled,
                pickup_enabled: services.pickup_enabled,
            }, sectionsOf(latest));
            if (Object.keys(body).length === 0) {
                toast.info("No changes to save");
            } else {
                await api.patch(`${apiPrefix}/restaurants/${rid}`, body);
                toast.success("Saved");
            }
            const { data } = await api.get(`${apiPrefix}/restaurants`);
            setRests(data || []);
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
    };

    if (rests.length === 0) {
        return <p className="text-muted-foreground">No shops yet.</p>;
    }
    if (!r) return <p className="text-muted-foreground">Loading…</p>;

    return (
        <div className="max-w-3xl" data-testid={`${testidPrefix}-page`}>
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-bold">Hours & Setup</h1>
                    <p className="text-sm text-muted-foreground mt-1">Business hours, printer & payout details</p>
                </div>
                {/* iter84 — searchable shop picker on Admin; on Merchant
                    the same UI degrades to a single-shop list (still
                    works fine, just shows one row). */}
                <AdminShopPicker
                    testIdPrefix={`${testidPrefix}-rest`}
                    value={rid}
                    onChange={setRid}
                    shopsOverride={rests}
                />
            </div>

            <section className="bg-card border border-border rounded-2xl p-5 mt-6">
                <div className="flex items-center justify-between mb-3">
                    <div>
                        <h3 className="font-bold">Business hours</h3>
                        <p className="text-[11px] text-muted-foreground">
                            Add multiple shifts per day to handle lunch + dinner with a break in between.
                        </p>
                    </div>
                    <button
                        type="button"
                        data-testid={`${testidPrefix}-copy-mon`}
                        onClick={copyMonToAll}
                        className="text-xs font-bold text-primary inline-flex items-center gap-1 hover:underline"
                    >
                        <Copy size={12} /> Copy Mon → All
                    </button>
                </div>
                <div className="space-y-3">
                    {DAYS.map(({ k, l }) => {
                        const d = hours[k] || { closed: false, shifts: [{ open: "09:00", close: "23:00" }] };
                        return (
                            <div key={k} className="border border-border rounded-xl p-3" data-testid={`${testidPrefix}-${k}`}>
                                <div className="flex items-center justify-between mb-2">
                                    <div className="font-semibold text-sm">{l}</div>
                                    <label className="text-xs inline-flex items-center gap-1.5 cursor-pointer">
                                        <input type="checkbox" className="accent-primary"
                                            data-testid={`${testidPrefix}-${k}-closed`}
                                            checked={!!d.closed}
                                            onChange={(e) => setDay(k, { closed: e.target.checked })} />
                                        Closed all day
                                    </label>
                                </div>
                                {!d.closed && (
                                    <div className="space-y-2">
                                        {(d.shifts || []).map((s, i) => (
                                            <div key={i} className="flex items-center gap-2" data-testid={`${testidPrefix}-${k}-shift-${i}`}>
                                                <input type="time" value={s.open}
                                                    onChange={(e) => updateShift(k, i, { open: e.target.value })}
                                                    className="bg-muted rounded-lg px-2 py-1 text-sm" />
                                                <span className="text-muted-foreground text-xs">→</span>
                                                <input type="time" value={s.close}
                                                    onChange={(e) => updateShift(k, i, { close: e.target.value })}
                                                    className="bg-muted rounded-lg px-2 py-1 text-sm" />
                                                {i > 0 && (
                                                    <button type="button"
                                                        data-testid={`${testidPrefix}-${k}-remove-${i}`}
                                                        onClick={() => removeShift(k, i)}
                                                        className="p-1 text-muted-foreground hover:text-rose-500"
                                                        title="Remove shift"
                                                    ><X size={14} /></button>
                                                )}
                                                {i === d.shifts.length - 1 && d.shifts.length < 3 && (
                                                    <button type="button"
                                                        data-testid={`${testidPrefix}-${k}-add-shift`}
                                                        onClick={() => addShift(k)}
                                                        className="ml-1 text-xs font-bold text-primary inline-flex items-center gap-1 hover:underline"
                                                    ><Plus size={12} /> Break / Shift</button>
                                                )}
                                            </div>
                                        ))}
                                        {d.shifts.length > 1 && (
                                            <p className="text-[10px] text-muted-foreground">
                                                Customers can&apos;t place orders / book services during the gap between shifts.
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </section>

            <section className="bg-card border border-border rounded-2xl p-5 mt-4">
                <h3 className="font-bold mb-3">Services offered</h3>
                <div className="space-y-2 text-sm">
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                        <input data-testid={`${testidPrefix}-hd-toggle`} type="checkbox" className="accent-primary w-4 h-4"
                            checked={services.home_delivery_enabled}
                            onChange={(e) => setServices({ ...services, home_delivery_enabled: e.target.checked })} />
                        <span className="font-semibold">Home Delivery</span>
                        <span className="text-xs text-muted-foreground">— customers can order delivery to their address</span>
                    </label>
                    <br />
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                        <input data-testid={`${testidPrefix}-pickup-toggle`} type="checkbox" className="accent-primary w-4 h-4"
                            checked={services.pickup_enabled}
                            onChange={(e) => setServices({ ...services, pickup_enabled: e.target.checked })} />
                        <span className="font-semibold">Store Pickup / Take Away</span>
                        <span className="text-xs text-muted-foreground">— customers can pick up the order in person</span>
                    </label>
                </div>
            </section>

            <section className="bg-card border border-border rounded-2xl p-5 mt-4">
                <h3 className="font-bold mb-3 inline-flex items-center gap-2"><Printer size={16} /> Printer integration</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                    <label><span className="text-xs font-semibold text-muted-foreground">Enabled</span>
                        <select data-testid={`${testidPrefix}-prn-enabled`} className="inp" value={printerCfg.enabled ? "1" : "0"} onChange={(e) => setPrinterCfg({ ...printerCfg, enabled: e.target.value === "1" })}><option value="0">Off</option><option value="1">On</option></select>
                    </label>
                    <label><span className="text-xs font-semibold text-muted-foreground">Paper</span>
                        <select className="inp" value={printerCfg.type || "THERMAL_80MM"} onChange={(e) => setPrinterCfg({ ...printerCfg, type: e.target.value })}><option>THERMAL_58MM</option><option>THERMAL_80MM</option><option>A4</option></select>
                    </label>
                    <label className="col-span-2"><span className="text-xs font-semibold text-muted-foreground">Printer name (local bridge)</span>
                        <input className="inp" value={printerCfg.printer_name || ""} onChange={(e) => setPrinterCfg({ ...printerCfg, printer_name: e.target.value })} placeholder="e.g. EPSON TM-T82" />
                    </label>
                    <label className="inline-flex items-center gap-2 col-span-2">
                        <input type="checkbox" checked={!!printerCfg.auto_print_on_new_order} onChange={(e) => setPrinterCfg({ ...printerCfg, auto_print_on_new_order: e.target.checked })} className="accent-primary" />
                        <span className="text-sm">Auto-print KOT on new order</span>
                    </label>
                </div>
            </section>

            <section className="bg-card border border-border rounded-2xl p-5 mt-4">
                <h3 className="font-bold mb-3">Payout account</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                    <label className="col-span-2"><span className="text-xs font-semibold text-muted-foreground">Account holder</span>
                        <input data-testid={`${testidPrefix}-po-holder`} className="inp" value={payout.account_holder || ""} onChange={(e) => setPayout({ ...payout, account_holder: e.target.value })} />
                    </label>
                    <label><span className="text-xs font-semibold text-muted-foreground">Account number</span>
                        <input className="inp" value={payout.account_number || ""} onChange={(e) => setPayout({ ...payout, account_number: e.target.value })} />
                    </label>
                    <label><span className="text-xs font-semibold text-muted-foreground">IFSC</span>
                        <input className="inp" value={payout.ifsc || ""} onChange={(e) => setPayout({ ...payout, ifsc: e.target.value })} />
                    </label>
                    <label><span className="text-xs font-semibold text-muted-foreground">Bank name</span>
                        <input className="inp" value={payout.bank_name || ""} onChange={(e) => setPayout({ ...payout, bank_name: e.target.value })} />
                    </label>
                    <label><span className="text-xs font-semibold text-muted-foreground">UPI ID</span>
                        <input className="inp" value={payout.upi_id || ""} onChange={(e) => setPayout({ ...payout, upi_id: e.target.value })} placeholder="name@bank" />
                    </label>
                </div>
            </section>

            <button data-testid={`${testidPrefix}-save`} disabled={busy} onClick={save} className="mt-5 bg-primary text-primary-foreground rounded-xl px-5 py-3 font-bold inline-flex items-center gap-2">
                {busy ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save
            </button>
            <style>{`.inp{display:block;background:hsl(var(--muted));border-radius:0.75rem;padding:0.6rem 0.75rem;outline:none;font-size:0.875rem;width:100%;margin-top:4px}`}</style>
        </div>
    );
}
